import { ImportOutlined, MailOutlined, SettingOutlined } from '@ant-design/icons'
import { PageHeader } from '@ant-design/pro-layout'
import { useMutation, useQuery, useSuspenseQuery } from '@apollo/client'
import {
  FetchMixedUsersDocument,
  type FetchMixedUsersQuery,
  FetchOrganizationDocument,
  FetchUserGroupsDocument,
  QueueImportSourceUsersQueueDocument,
  type UserGroup,
  UserLicenseTypeEnum,
  UserPlanEnum,
} from '@salescore/client-api'
import { getOrganizationIdFromPath, SuspenseWithLoading, useCanForFeatures } from '@salescore/client-common'
import {
  useCreateUserRoleOptions,
  useFetchJobStatus,
  USER_PROVIDER_JA,
  UsersTable,
} from '@salescore/client-feature-setting'
import { BUILTIN_USER_ROLES } from '@salescore/features'
import { CsvDownloadButton } from '@salescore/frontend-common'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Alert, Button, Dropdown, Menu, message } from 'antd'
import { Array, pipe } from 'effect'
import { isNotNullish } from 'effect/Predicate'
import { t } from 'i18next'
import { useEffect } from 'react'

import { sortUsersForCsv } from '../../../../../components/sortUsersForCsv.ts'

type MixedUser = FetchMixedUsersQuery['mixedUsers'][0]

const Page = () => {
  const navigate = useNavigate()
  const organizationId = getOrganizationIdFromPath()
  const [queueImportSourceUsersQueue] = useMutation(QueueImportSourceUsersQueueDocument)
  const jobStatus = useFetchJobStatus()
  const { canManageUsers, canManageInvitation } = useCanForFeatures()
  const { data: mixedUserData } = useSuspenseQuery(FetchMixedUsersDocument, {
    variables: {
      organizationId: getOrganizationIdFromPath(),
    },
  })
  const { data: userGroupData } = useSuspenseQuery(FetchUserGroupsDocument, {
    variables: { organizationId: getOrganizationIdFromPath() },
  })
  const roles = useCreateUserRoleOptions()
  const userGroupHeaders = useCsvUserGroupHeaders()

  useEffect(() => {
    if (!jobStatus.polling && jobStatus.jobStatuses.length > 0) {
      void message.success(t(`同期が完了しました`))
    }
  }, [jobStatus.jobStatuses])

  if (!canManageUsers) {
    return (
      <div>
        <Alert type="warning" message={t(`権限がありません`)} />
      </div>
    )
  }

  return (
    <PageHeader
      title={t(`ユーザー一覧`)}
      extra={[
        <CsvDownloadButton
          key="user-csv-download"
          data={createCsvData(userGroupHeaders, mixedUserData.mixedUsers, userGroupData.userGroups, roles)}
          filename={t(`ユーザー一覧`)}
          headers={[
            ...userGroupHeaders,
            t(`ユーザー名`),
            `email`,
            t(`招待`),
            t(`ライセンス(SYNC)`),
            t(`ライセンス(VISUALIZE)`),
            t(`権限`),
            t(`ログイン方法`),
          ]}
        />,
        <Dropdown
          key="dropdown"
          // eslint-disable-next-line sonarjs/deprecation
          overlay={
            <Menu
              onClick={() => {
                // empty
              }}
            >
              <Menu.Item
                key="1"
                icon={<ImportOutlined />}
                // eslint-disable-next-line @typescript-eslint/no-misused-promises,@typescript-eslint/strict-void-return
                onClick={async () => {
                  const result = await queueImportSourceUsersQueue({
                    variables: { organizationId: getOrganizationIdFromPath() },
                  })
                  void jobStatus.fetchStart({
                    id: result.data?.queueImportSourceUsersQueue?.id ?? undefined,
                    jobName: 'ImportSourceUsersJob',
                  })
                  void message.success(t(`同期を開始しました`))
                }}
              >
                {t(`連携先ユーザーの再同期`)}
              </Menu.Item>
            </Menu>
          }
        >
          <Button icon={<SettingOutlined />} />
        </Dropdown>,
        <Button
          key="button-to-invitation"
          icon={<MailOutlined />}
          type="default"
          disabled={!canManageInvitation}
          onClick={() => {
            void navigate({ to: '/o/$organizationId/v2/settings/invitations', params: { organizationId } })
          }}
        >
          {t(`ユーザー招待へ`)}
        </Button>,
      ]}
    >
      <SuspenseWithLoading type="table">
        <UsersTable />
      </SuspenseWithLoading>
    </PageHeader>
  )
}

function createCsvData(
  userGroupHeaders: string[],
  users: MixedUser[],
  userGroups: UserGroup[],
  roles: Array<{ value: string; label: string | null | undefined }>,
) {
  const sortedUsers = pipe(
    users,
    Array.filter((x) => x.user?.role !== BUILTIN_USER_ROLES.admin),
    sortUsersForCsv,
  )
  const userGroupsMap = new Map(userGroups.map((x) => [x.id, x]))
  const rolesMap = new Map(roles.map((x) => [x.value, x.label]))

  const getPlan = (
    sortedUser: MixedUser,
    licenseType: UserLicenseTypeEnum,
    invitationPlan: string | undefined,
  ): string | undefined =>
    isNotNullish(sortedUser.user)
      ? (sortedUser.user.userLicenses.find((x) => x.license === licenseType)?.plan ?? undefined)
      : (invitationPlan ?? undefined)
  const getPlanLabel = (plan: string | undefined, name: string) =>
    // eslint-disable-next-line sonarjs/no-nested-conditional
    plan === UserPlanEnum.Standard ? name : plan === UserPlanEnum.Free ? t(`${name}(無料)`) : `-`

  return sortedUsers.map((x) => {
    const syncPlan = getPlan(x, UserLicenseTypeEnum.Sync, x.invitation?.planForSync)
    const visPlan = getPlan(x, UserLicenseTypeEnum.Visualize, x.invitation?.planForVis)

    const syncPlanLabel = getPlanLabel(syncPlan, 'SYNC')
    const visPlanLabel = getPlanLabel(visPlan, 'VISUALIZE')

    const provider = x.user?.identity.provider ?? x.invitation?.provider
    return [
      ...createCsvUserGroupColumns(userGroupHeaders, userGroupsMap, x.resourceUser.user_group_id),
      x.resourceUser.name,
      x.resourceUser.email,
      (x.user === undefined || x.user === null) && isNotNullish(x.invitation) ? t(`招待中`) : '',
      isNotNullish(x.user) || isNotNullish(x.invitation) ? syncPlanLabel : '',
      isNotNullish(x.user) || isNotNullish(x.invitation) ? visPlanLabel : '',
      rolesMap.get(x.user?.role ?? x.invitation?.role ?? '') ?? t(`連携先ユーザー`),
      provider === undefined ? '' : USER_PROVIDER_JA[provider],
    ]
  })
}

function createCsvUserGroupColumns(
  userGroupHeaders: string[],
  userGroupsMap: Map<string, UserGroup>,
  userGroupId: string | null | undefined,
) {
  if (userGroupHeaders.length === 0) {
    // 階層が全て消去されている場合
    return []
  }
  // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
  if (!userGroupId) {
    const emptyFillCount = Math.max(0, userGroupHeaders.length - 1)
    return [t(`所属なし`), ...(emptyFillCount === 0 ? [] : Array.replicate('', emptyFillCount))]
  }
  /**
   * @returns ユーザーが所属するユーザーグループの各階層ごとのマップ
   */
  const getUserGroupDepthMap = (userGroupId: string, map?: Record<number, UserGroup>): Record<number, UserGroup> => {
    const userGroup = userGroupsMap.get(userGroupId)
    if (userGroup === undefined) {
      // eslint-disable-next-line sonarjs/todo-tag
      // TODO: SQLのリレーション的に起こり得ないはず
      throw new Error(`Invalid UserGroup ID: ${userGroupId}`)
    }

    const updatedMap = { ...map, [userGroup.depth]: userGroup }
    return userGroup.user_group_id === undefined || userGroup.user_group_id === null
      ? updatedMap
      : getUserGroupDepthMap(userGroup.user_group_id, updatedMap)
  }

  const userGroupDepthMap = getUserGroupDepthMap(userGroupId)

  return (userGroupHeaders.length > 0 ? Array.range(1, userGroupHeaders.length) : Array.empty<number>()).map(
    (index) => userGroupDepthMap[index]?.name ?? '',
  )
}

function useCsvUserGroupHeaders() {
  const organizationSettingQuery = useQuery(FetchOrganizationDocument, {
    variables: { organizationId: getOrganizationIdFromPath() },
  })
  return (
    organizationSettingQuery.data?.organization.setting.userGroupCategoryNames.map(
      (name) => `${t(`グループ名`)}(${name})`,
    ) ?? []
  )
}

export const Route = createFileRoute('/o/$organizationId/v2/settings/users')({
  component: Page,
})
